package dev.kuml.portal.web.auth

import dev.kuml.portal.web.AppScope
import dev.kuml.portal.web.api.ApiResult
import dev.kuml.portal.web.api.PortalApi
import dev.kuml.portal.web.components.languageSwitcher
import dev.kuml.portal.web.components.legalLinks
import dev.kuml.portal.web.i18n.t
import io.kvision.core.Container
import io.kvision.form.text.TextInput
import io.kvision.form.text.textInput
import io.kvision.html.Button
import io.kvision.html.Div
import io.kvision.html.InputType
import io.kvision.html.button
import io.kvision.html.div
import io.kvision.html.image
import kotlinx.browser.window
import kotlinx.coroutines.launch

private enum class AuthMode { LOGIN, REGISTER }

/** Mirrors the server's `UserService.MIN_PASSWORD_LENGTH` -- enforced client-side ONLY in
 * [AuthMode.REGISTER] (see [validatePassword] below), so a too-short password is caught before the
 * round-trip instead of bouncing back the server's generic `password_too_short` rejection. LOGIN
 * intentionally stays a bare non-empty check: an existing account's password was valid under
 * whatever rule applied when it was created and must always be allowed to authenticate.
 *
 * `internal`, not `private` -- also reused by `dev.kuml.portal.web.auth.resetPasswordScreen`
 * (R-4), which enforces the SAME rule (`PasswordResetService.confirm` -> `validateNewPassword` is
 * word-for-word `UserService.register`'s check, see that service's KDoc). */
internal const val MIN_PASSWORD_LENGTH = 8

/**
 * Auth screen (design spec, section 1) -- UNVERAENDERT ggue. Runde 2/3. Centered card, a single
 * tab-pair control switches Anmelden/Registrieren (no redundant text link), E-Mail + Passwort,
 * inline field errors validated at blur/submit (not on every keystroke), Enter in the last field
 * submits, the submit button becomes a same-width loading state and is locked against double-click.
 *
 * NOTE on `displayName`: the server's `POST /api/auth/register` requires a non-blank `displayName`
 * (see `kuml-portal-server`'s `RegisterRequest`), but the approved design deliberately shows no
 * field beyond E-Mail/Passwort for Register ("Registrieren zeigt nichts Zusaetzliches (MVP)"). To
 * honor the approved design exactly while still satisfying the server contract, the display name is
 * derived silently from the local part of the email address (the substring before "@") -- a
 * settings screen to rename the account later is out of scope for this wave.
 *
 * R-18: this is the FIRST screen an unauthenticated visitor sees, and has no
 * [dev.kuml.portal.web.components.portalHeader] -- so it carries its own
 * [dev.kuml.portal.web.components.languageSwitcher] next to [legalLinks], rather than relying on
 * a header that doesn't exist here yet (see this wave's plan §4.2).
 *
 * @param initialNotice an unaufdringlich status line shown once on entry (e.g. the "Ihre Sitzung
 *   ist abgelaufen" copy after a 401 elsewhere bounced the user here, or an R-5
 *   [dev.kuml.portal.web.auth.OAuthNotice]'s message after an OAuth callback redirect) -- `null`
 *   shows nothing.
 * @param initialNoticeIsError whether [initialNotice] renders in the error style (see
 *   [dev.kuml.portal.web.auth.OAuthNotice.isError]) -- the pre-R-5 session-expiry notice is always
 *   neutral, so this defaults to `false` for every pre-existing call site.
 * @param onImpressumClick / @param onDatenschutzClick / @param onAgbClick navigate to the three
 *   legally required pages (§ 5 TMG / Art. 13 DSGVO / AGB) -- this is the first screen any
 *   unauthenticated visitor sees, so the link row lives directly under the card, not hidden behind
 *   a login.
 */
public fun Container.authScreen(
    api: PortalApi,
    initialNotice: String?,
    initialNoticeIsError: Boolean = false,
    onAuthenticated: () -> Unit,
    onImpressumClick: () -> Unit,
    onDatenschutzClick: () -> Unit,
    onAgbClick: () -> Unit,
) {
    var mode = AuthMode.LOGIN
    var submitting = false

    lateinit var loginTab: Button
    lateinit var registerTab: Button
    lateinit var emailInput: TextInput
    lateinit var emailError: Div
    lateinit var passwordInput: TextInput
    lateinit var passwordError: Div
    lateinit var confirmPasswordField: Div
    lateinit var confirmPasswordInput: TextInput
    lateinit var confirmPasswordError: Div
    lateinit var submitButton: Button
    lateinit var statusDiv: Div

    // ---------- Forgot-password mini form (R-4) -- LOGIN-mode-only, toggled in place of [formHost]
    // rather than a separate route, so a slip of the finger never loses whatever was already typed
    // into the login form (see [showForgotPassword]/[showLoginForm] below). ----------
    lateinit var formHost: Div
    lateinit var forgotLinkRow: Div
    lateinit var forgotHost: Div
    lateinit var forgotEmailInput: TextInput
    lateinit var forgotStatusDiv: Div
    lateinit var forgotSubmitButton: Button
    var forgotSubmitting = false

    // ---------- R-5: Social Login -- shown under BOTH Anmelden and Registrieren (the server's
    // POST-equivalent /start endpoint already registers-or-logs-in transparently on first use, so
    // there is no separate "sign up with Google" vs. "log in with Google" distinction to make in
    // this UI). One button per provider the server reports as configured (GET
    // /api/auth/oauth/providers, fetched once at mount below). Hidden entirely (both the divider
    // and the button row) when the server reports no configured provider, e.g. local dev without
    // secrets -- there is nothing useful to click. ----------
    lateinit var oauthDivider: Div
    lateinit var oauthButtonsHost: Div

    fun validateEmail(): Boolean {
        val value = emailInput.value?.trim().orEmpty()
        val valid = value.isNotEmpty() && value.contains("@")
        emailError.content = if (valid) "" else t.auth.emailInvalid
        if (valid) emailInput.removeCssClass("kportal-input-error") else emailInput.addCssClass("kportal-input-error")
        return valid
    }

    fun validatePassword(): Boolean {
        val value = passwordInput.value.orEmpty()
        // REGISTER also enforces MIN_PASSWORD_LENGTH client-side (see that constant's KDoc) --
        // LOGIN stays a bare non-empty check so an existing account's password always verifies.
        val tooShort = mode == AuthMode.REGISTER && value.isNotEmpty() && value.length < MIN_PASSWORD_LENGTH
        val valid = value.isNotEmpty() && !tooShort
        passwordError.content =
            when {
                valid -> ""
                value.isEmpty() -> t.auth.passwordRequired
                else -> t.auth.passwordTooShort
            }
        if (valid) passwordInput.removeCssClass("kportal-input-error") else passwordInput.addCssClass("kportal-input-error")
        return valid
    }

    fun validateConfirmPassword(): Boolean {
        if (mode == AuthMode.LOGIN) return true
        val value = confirmPasswordInput.value.orEmpty()
        val valid = value.isNotEmpty() && value == passwordInput.value.orEmpty()
        confirmPasswordError.content = if (valid) "" else t.auth.passwordsDoNotMatch
        if (valid) {
            confirmPasswordInput.removeCssClass("kportal-input-error")
        } else {
            confirmPasswordInput.addCssClass("kportal-input-error")
        }
        return valid
    }

    fun setStatus(
        message: String,
        isError: Boolean,
    ) {
        statusDiv.content = message
        if (isError) {
            statusDiv.addCssClass("kportal-status-error")
            statusDiv.removeCssClass("kportal-status-neutral")
        } else {
            statusDiv.addCssClass("kportal-status-neutral")
            statusDiv.removeCssClass("kportal-status-error")
        }
    }

    fun applyMode() {
        if (mode == AuthMode.LOGIN) {
            loginTab.addCssClass("active")
            registerTab.removeCssClass("active")
            submitButton.text = t.auth.submitLogin
            confirmPasswordField.addCssClass("kportal-hidden")
            forgotLinkRow.removeCssClass("kportal-hidden")
        } else {
            registerTab.addCssClass("active")
            loginTab.removeCssClass("active")
            submitButton.text = t.auth.submitRegister
            confirmPasswordField.removeCssClass("kportal-hidden")
            forgotLinkRow.addCssClass("kportal-hidden")
        }
        emailError.content = ""
        passwordError.content = ""
        confirmPasswordInput.value = ""
        confirmPasswordError.content = ""
        confirmPasswordInput.removeCssClass("kportal-input-error")
        setStatus("", isError = false)
    }

    fun setSubmitting(value: Boolean) {
        submitting = value
        submitButton.disabled = value
        submitButton.text =
            when {
                value && mode == AuthMode.LOGIN -> t.auth.submitLoginBusy
                value -> t.auth.submitRegisterBusy
                mode == AuthMode.LOGIN -> t.auth.submitLogin
                else -> t.auth.submitRegister
            }
    }

    fun submit() {
        if (submitting) return
        val emailOk = validateEmail()
        val passwordOk = validatePassword()
        val confirmPasswordOk = validateConfirmPassword()
        if (!emailOk || !passwordOk || !confirmPasswordOk) return

        val email = emailInput.value!!.trim()
        val password = passwordInput.value!!
        setSubmitting(true)
        AppScope.launch {
            val result =
                if (mode == AuthMode.LOGIN) {
                    api.login(email, password)
                } else {
                    val displayName = email.substringBefore("@").ifBlank { t.auth.defaultDisplayName }
                    api.register(email, password, displayName)
                }
            setSubmitting(false)
            when (result) {
                is ApiResult.Success -> onAuthenticated()
                is ApiResult.HttpError -> {
                    when {
                        mode == AuthMode.LOGIN && result.status == 401 -> setStatus(t.auth.wrongCredentials, isError = true)
                        mode == AuthMode.REGISTER && result.status == 409 -> {
                            mode = AuthMode.LOGIN
                            applyMode()
                            setStatus(t.auth.accountAlreadyExists, isError = true)
                        }
                        // R-18-major-finding-1: register's 400s (password/email/display-name
                        // rejections, see `UserService.register`'s `code`s) now carry a
                        // machine-readable `code` -- prefer the specific translated message over
                        // the generic "try again" copy whenever one is available.
                        else ->
                            setStatus(
                                t.errors.forCode(result.error?.code, result.status) ?: t.auth.genericAuthFailure,
                                isError = true,
                            )
                    }
                }
                ApiResult.NetworkError -> setStatus(t.common.networkErrorRetry, isError = true)
            }
        }
    }

    fun setForgotStatus(
        message: String,
        isError: Boolean,
    ) {
        forgotStatusDiv.content = message
        if (isError) {
            forgotStatusDiv.addCssClass("kportal-status-error")
            forgotStatusDiv.removeCssClass("kportal-status-neutral")
        } else {
            forgotStatusDiv.addCssClass("kportal-status-neutral")
            forgotStatusDiv.removeCssClass("kportal-status-error")
        }
    }

    fun showLoginForm() {
        forgotHost.addCssClass("kportal-hidden")
        formHost.removeCssClass("kportal-hidden")
    }

    fun showForgotPassword() {
        formHost.addCssClass("kportal-hidden")
        forgotHost.removeCssClass("kportal-hidden")
        // Convenience: carry over whatever the caller already typed into the login field, rather
        // than making them retype an address they just entered one field up.
        forgotEmailInput.value = emailInput.value
        setForgotStatus("", isError = false)
    }

    fun submitForgotPassword() {
        if (forgotSubmitting) return
        val email = forgotEmailInput.value?.trim().orEmpty()
        if (email.isEmpty() || !email.contains("@")) {
            setForgotStatus(t.auth.emailInvalid, isError = true)
            return
        }
        forgotSubmitting = true
        forgotSubmitButton.disabled = true
        forgotSubmitButton.text = t.auth.forgotPasswordSubmitBusy
        AppScope.launch {
            val result = api.requestPasswordReset(email)
            forgotSubmitting = false
            forgotSubmitButton.disabled = false
            forgotSubmitButton.text = t.auth.forgotPasswordSubmit
            when (result) {
                // `POST /api/auth/password-reset/request` NEVER answers anything but 202/429 (see
                // `dev.kuml.portal.server.auth.passwordResetRoutes`'s "No account-enumeration leak"
                // KDoc) -- Success and every HttpError OTHER than 429 therefore get the SAME neutral
                // copy, so a stray unexpected status can never become an oracle either.
                is ApiResult.Success -> setForgotStatus(t.auth.forgotPasswordSuccess, isError = false)
                is ApiResult.HttpError ->
                    if (result.status == 429) {
                        setForgotStatus(t.errors.rateLimited(result.retryAfterSeconds), isError = true)
                    } else {
                        setForgotStatus(t.auth.forgotPasswordSuccess, isError = false)
                    }
                ApiResult.NetworkError -> setForgotStatus(t.common.networkErrorRetry, isError = true)
            }
        }
    }

    div(className = "kportal-auth-shell") {
        div(className = "kportal-auth-card") {
            div(className = "kportal-wordmark") {
                image(src = "img/logo.svg", alt = "kUML").addCssClass("kportal-logo-light")
                image(src = "img/logo-negative.svg", alt = "kUML").addCssClass("kportal-logo-dark")
            }

            formHost =
                div {
                    div(className = "kportal-tabs") {
                        loginTab = button(t.auth.loginTab, className = "kportal-tab active")
                        loginTab.onClick {
                            mode = AuthMode.LOGIN
                            applyMode()
                        }
                        registerTab = button(t.auth.registerTab, className = "kportal-tab")
                        registerTab.onClick {
                            mode = AuthMode.REGISTER
                            applyMode()
                        }
                    }

                    if (initialNotice != null) {
                        val noticeClass = if (initialNoticeIsError) "kportal-status-error" else "kportal-status-neutral"
                        div(initialNotice, className = "$noticeClass kportal-status-area")
                    }

                    div(className = "kportal-field") {
                        div(t.auth.emailLabel, className = "kportal-label")
                        emailInput = textInput(type = InputType.EMAIL, className = "kportal-input")
                        emailError = div(className = "kportal-field-error")
                    }

                    div(className = "kportal-field") {
                        div(t.auth.passwordLabel, className = "kportal-label")
                        passwordInput = textInput(type = InputType.PASSWORD, className = "kportal-input")
                        passwordError = div(className = "kportal-field-error")
                    }

                    // R-4: LOGIN-mode-only ([applyMode] hides it in REGISTER) -- swaps [formHost]
                    // out for [forgotHost] via [showForgotPassword].
                    forgotLinkRow =
                        div(className = "kportal-auth-forgot-link") {
                            val link = button(t.auth.forgotPasswordLink, className = "kportal-legal-link")
                            link.onClick { showForgotPassword() }
                        }

                    confirmPasswordField =
                        div(className = "kportal-field kportal-hidden") {
                            div(t.auth.confirmPasswordLabel, className = "kportal-label")
                            confirmPasswordInput = textInput(type = InputType.PASSWORD, className = "kportal-input")
                            confirmPasswordError = div(className = "kportal-field-error")
                        }

                    submitButton = button(t.auth.submitLogin, className = "kportal-btn kportal-btn-primary")
                    submitButton.onClick { submit() }
                    statusDiv = div(className = "kportal-status-area") { setAttribute("aria-live", "polite") }

                    // R-5: hidden by default (kportal-hidden) -- shown after the async provider
                    // fetch below resolves to a non-empty list, never before (see this file's
                    // top-level KDoc block on [oauthDivider]/[oauthButtonsHost]).
                    oauthDivider =
                        div(className = "kportal-auth-divider kportal-hidden") {
                            div(t.auth.orDivider, className = "kportal-auth-divider-label")
                        }
                    oauthButtonsHost = div(className = "kportal-auth-oauth-buttons kportal-hidden")
                }

            forgotHost =
                div(className = "kportal-hidden") {
                    div(t.auth.forgotPasswordTitle, className = "kportal-auth-title")
                    div(t.auth.forgotPasswordIntro, className = "kportal-auth-message")
                    div(className = "kportal-field") {
                        div(t.auth.emailLabel, className = "kportal-label")
                        forgotEmailInput = textInput(type = InputType.EMAIL, className = "kportal-input")
                    }
                    forgotSubmitButton = button(t.auth.forgotPasswordSubmit, className = "kportal-btn kportal-btn-primary")
                    forgotSubmitButton.onClick { submitForgotPassword() }
                    forgotStatusDiv = div(className = "kportal-status-area") { setAttribute("aria-live", "polite") }
                    div(className = "kportal-auth-back-link") {
                        val back = button(t.auth.backToLoginLink, className = "kportal-legal-link")
                        back.onClick { showLoginForm() }
                    }
                }
        }
        div(className = "kportal-auth-footer") {
            legalLinks(onImpressumClick, onDatenschutzClick, onAgbClick)
            languageSwitcher()
        }
    }

    emailInput.setEventListener<TextInput> {
        blur = { validateEmail() }
    }
    passwordInput.setEventListener<TextInput> {
        blur = { validatePassword() }
        keydown = { e ->
            if (e.key == "Enter") {
                e.preventDefault()
                submit()
            }
        }
    }
    confirmPasswordInput.setEventListener<TextInput> {
        blur = { validateConfirmPassword() }
        keydown = { e ->
            if (e.key == "Enter") {
                e.preventDefault()
                submit()
            }
        }
    }
    forgotEmailInput.setEventListener<TextInput> {
        keydown = { e ->
            if (e.key == "Enter") {
                e.preventDefault()
                submitForgotPassword()
            }
        }
    }

    // R-5: loaded once at mount, exactly like `dev.kuml.portal.web.profile.profileScreen`'s own
    // `reload()` call at the very end of that function. A failed/empty response leaves the
    // divider+buttons in their hidden constructor state -- never a visible, broken "no providers"
    // section (see [oauthDivider]/[oauthButtonsHost]'s KDoc).
    AppScope.launch {
        when (val result = api.oauthProviders()) {
            is ApiResult.Success -> {
                val providers = result.value.providers
                if (providers.isNotEmpty()) {
                    providers.forEach { providerId ->
                        val label =
                            when (providerId) {
                                "google" -> t.auth.signInWithGoogle
                                "github" -> t.auth.signInWithGitHub
                                else -> return@forEach
                            }
                        val providerButton =
                            oauthButtonsHost.button(label, className = "kportal-btn kportal-btn-oauth kportal-btn-oauth-$providerId")
                        providerButton.onClick { window.location.assign("/api/auth/oauth/$providerId/start") }
                    }
                    oauthDivider.removeCssClass("kportal-hidden")
                    oauthButtonsHost.removeCssClass("kportal-hidden")
                }
            }
            else -> Unit // stays hidden -- see this block's own KDoc above.
        }
    }
}
